import {CARD_ICON_B64, PAYPAL_ICON_B64} from './payment-icons'

const ADYEN_API_VARS = {
    // Background colors
    '--adyen-sdk-color-background-primary': 'var(--chakra-colors-webTheme-backgroundDefault)',
    '--adyen-sdk-color-background-secondary': 'var(--chakra-colors-webTheme-backgroundDefault)',
    '--adyen-sdk-color-background-disabled': 'var(--chakra-colors-webTheme-backgroundPrimaryBrand)',
    // Borders
    '--adyen-sdk-border-width-s': '1px',
    '--adyen-sdk-border-radius-m': '0px',
    // Outline
    '--adyen-sdk-color-outline-tertiary': 'var(--chakra-colors-webTheme-strokeTertiary)',
    '--adyen-sdk-color-outline-secondary': 'var(--chakra-colors-webTheme-strokeTertiary)',
    '--adyen-sdk-color-outline-primary-active': 'transparent',
    // Spacers
    '--adyen-sdk-spacer-110': '48px',
    // Labels
    '--adyen-sdk-color-label-primary': 'var(--chakra-colors-webTypo-textPrimary)',
    '--adyen-sdk-color-label-secondary': 'var(--chakra-colors-webTypo-textPrimary)',
    '--adyen-sdk-color-label-tertiary': 'var(--chakra-colors-webTypo-textPrimary)',
    '--adyen-sdk-color-label-critical': 'var(--chakra-colors-webTypo-textError)',
    // Custom payment method icons (stored as CSS vars to bypass Chakra transforms)
    '--custom-card-icon': `url("data:image/svg+xml;base64,${CARD_ICON_B64}")`,
    '--custom-paypal-icon': `url("data:image/svg+xml;base64,${PAYPAL_ICON_B64}")`,
    '--custom-error-icon': `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='%23B30000'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M20 0V20H0V0H20ZM10.51 13.4997H9.5L9.51 14.4997H10.51V13.4997ZM10.5055 6H9.5045L9.5095 11.499H10.5095L10.5055 6Z' fill='%23B30000'/%3E%3C/svg%3E")`,
    '--custom-cvv-icon': `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 9H22M7.5 15H5M4 4H20C21.104 4 22 4.896 22 6V18C22 19.104 21.104 20 20 20H4C2.896 20 2 19.104 2 18V6C2 4.896 2.896 4 4 4Z' stroke='black' stroke-linecap='square'/%3E%3C/svg%3E")`
}
const RESET_STYLING = {
    // Hide inline validation icons (checkmarks/crosses)
    '.adyen-checkout__input-wrapper .adyen-checkout-input__inline-validation': {
        display: 'none'
    },
    // Hide card brand logos under the card number field
    '.adyen-checkout__card__brands': {
        display: 'none'
    }
}

const SECTION_HEADER_STYLING = (theme) => ({
    '.adyen-checkout-payment-methods-list-label': {
        ...theme?.textStyles?.webH4,
        display: 'block'
    },
    '.adyen-checkout-payment-methods-container': {
        gap: 3
    }
})

const GLOBAL_STYLES = {
    '.adyen-checkout__dropin': {
        gap: {base: 12, lg: 16}
    },
    '.adyen-checkout__payment-methods-list': {
        gap: '8px'
    },
    '.adyen-checkout__payment-method': {
        borderRadius: 0,
        '&:hover': {
            borderColor: 'inherit !important'
        }
    }
}

const THIRD_PARTY_SELECTED_PANEL_STYLING = {
    '.adyen-checkout__payment-method--selected.adyen-checkout__payment-method--paypal, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--klarna, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--klarna_account, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--klarna_paynow, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--applepay, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--googlepay': {
        backgroundColor: 'webTheme.backgroundPrimaryBrand',
        '.adyen-checkout__payment-method__header, .adyen-checkout-pm-details-wrapper, .adyen-checkout__payment-method__details, .adyen-checkout__payment-method__details__content': {
            backgroundColor: 'webTheme.backgroundPrimaryBrand'
        }
    }
}

const getPaymentButtonStyling = (theme) => {
    const buttonTypography = theme?.textStyles?.webButtonTypography || {
        fontSize: {base: 'webMd'},
        lineHeight: {base: '20px'},
        fontWeight: 'bold',
        textTransform: 'uppercase'
    }

    return {
        // Klarna "Pay with Klarna" button — fully stylable
        '.adyen-checkout__button--pay': {
            ...buttonTypography,
            height: '48px',
            minHeight: '48px',
            borderRadius: '0 !important',
            backgroundColor: 'webButton.btnPrimaryDefault !important',
            color: 'webTypo.textPrimaryReverse !important',
            border: 'none !important',
            cursor: 'pointer',
            width: '100%',
            '&:hover': {
                backgroundColor: 'webButton.btnPrimaryHover !important'
            },
            '&:focus-visible': {
                boxShadow: '0 0 0 2px var(--chakra-colors-webTheme-focus) !important'
            }
        },
        // Google Pay button container
        '.adyen-checkout__paywithgoogle': {
            '.gpay-card-info-container-fill': {
                height: '48px',
                minHeight: '48px'
            },
            '.gpay-card-info-container': {
                height: '48px !important',
                minHeight: '48px !important',
                borderRadius: '0 !important'
            }
        },
        // PayPal button container
        '.adyen-checkout__paypal__button--paypal': {
            height: '48px',
            minHeight: '48px',
            '& > div': {
                height: '48px !important',
                minHeight: '48px !important'
            }
        },
        // Hide PayPal credit, pay later, venmo buttons
        '.adyen-checkout__paypal__button--credit, .adyen-checkout__paypal__button--pay-later, .adyen-checkout__paypal__button--venmo': {
            display: 'none'
        }
    }
}

const RADIO_STYLING = {
    // Radio button: match design system (16x16, black border, white bg, 8x8 inner dot)
    '.adyen-checkout__payment-method__radio': {
        width: '16px !important',
        height: '16px !important',
        minWidth: '16px',
        margin: '4px',
        borderWidth: '1px !important',
        borderStyle: 'solid !important',
        borderRadius: '50% !important',
        borderColor: 'var(--chakra-colors-webTheme-iconPrimary) !important',
        backgroundColor: 'var(--chakra-colors-webTheme-backgroundDefault) !important',
        backgroundImage: 'none !important',
        backgroundSize: 'auto !important',
        boxShadow: 'none !important',
        flexShrink: 0,
        appearance: 'none',
        order: 0,
        '&.adyen-checkout__payment-method__radio--selected': {
            borderColor: 'var(--chakra-colors-webTheme-iconPrimary) !important',
            backgroundColor: 'var(--chakra-colors-webTheme-backgroundDefault) !important',
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
            // Inner dot using radial-gradient
            background: 'radial-gradient(circle, var(--chakra-colors-webTheme-iconPrimary) 4px, var(--chakra-colors-webTheme-backgroundDefault) 4px) !important',
            '&::after': {
                display: 'none !important'
            }
        },
        // Hide SDK's animated ::after on all states
        '&::after': {
            display: 'none !important'
        }
    }
}

const HEADER_LAYOUT_STYLING = (theme) => ({
    // Reorder: radio → name → image (image moves to right of name)
    '.adyen-checkout__payment-method__header__title': {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: '8px'
    },
    '.adyen-checkout__payment-method__name_wrapper': {
        order: 1,
        marginLeft: '4px'
    },
    // Payment method name: bodyXsSRegular + textSecondary (unselected)
    '.adyen-checkout__payment-method__name': {
        ...(theme?.textStyles?.bodyXsSRegular || {}),
        color: 'webTypo.textSecondary !important'
    },
    // Payment method name: bodyXsSRegular (selected — default text color)
    '.adyen-checkout__payment-method--selected .adyen-checkout__payment-method__name': {
        ...(theme?.textStyles?.bodyXsSRegular || {}),
        color: 'webTypo.textPrimary !important'
    },
    '.adyen-checkout__payment-method__image__wrapper': {
        order: 2,
        marginLeft: 'auto',
        boxShadow: 'none !important'
    },
    // Klarna tab name override (uses CSS var set from component with translated text)
    '.adyen-checkout__payment-method--klarna .adyen-checkout__payment-method__name, .adyen-checkout__payment-method--klarna_account .adyen-checkout__payment-method__name': {
        fontSize: '0 !important',
        lineHeight: '0 !important',
        '&::after': {
            content: 'var(--klarna-tab-name)',
            ...(theme?.textStyles?.bodyXsSRegular || {}),
            color: 'webTypo.textSecondary',
        }
    },
    '.adyen-checkout__payment-method--selected.adyen-checkout__payment-method--klarna .adyen-checkout__payment-method__name, .adyen-checkout__payment-method--selected.adyen-checkout__payment-method--klarna_account .adyen-checkout__payment-method__name': {
        '&::after': {
            color: 'webTypo.textPrimary',
        }
    }
})

const ICON_STYLING = {
    // Custom card brands icon (Visa/MC/Amex combined)
    '.adyen-checkout__payment-method--scheme .adyen-checkout__payment-method__image__wrapper': {
        display: 'inline-block',
        backgroundImage: 'var(--custom-card-icon)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        width: '93px',
        height: '18px'
    },
    '.adyen-checkout__payment-method--scheme .adyen-checkout__payment-method__image': {
        display: 'none'
    },
    // Custom PayPal icon
    '.adyen-checkout__payment-method--paypal .adyen-checkout__payment-method__image__wrapper': {
        display: 'inline-block',
        backgroundImage: 'var(--custom-paypal-icon)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        width: '66px',
        height: '16px'
    },
    '.adyen-checkout__payment-method--paypal .adyen-checkout__payment-method__image': {
        display: 'none'
    },
    // Klarna logo sizing
    '.adyen-checkout__payment-method--klarna .adyen-checkout__payment-method__image__wrapper, .adyen-checkout__payment-method--klarna_account .adyen-checkout__payment-method__image__wrapper': {
        width: '57px',
        height: '24px'
    },
    '.adyen-checkout__payment-method--klarna .adyen-checkout__payment-method__image, .adyen-checkout__payment-method--klarna_account .adyen-checkout__payment-method__image': {
        width: '57px',
        height: 'auto'
    }
}

const getFloatingLabelDefault = (theme) => ({
    position: 'absolute !important',
    top: '14px',
    left: '9px',
    zIndex: 2,
    pointerEvents: 'none',
    margin: 0,
    padding: 0,
    transformOrigin: 'left top',
    transition: 'transform 0.15s ease-in-out, top 0.15s ease-in-out',
    transform: 'scale(1)',
    '.adyen-checkout__label__text': {
        ...(theme?.textStyles?.bodySmRegular || {}),
        color: 'webTypo.textSecondary'
    }
})

const FLOATING_LABEL_ACTIVE = {
    top: '4px',
    left: '9px',
    transform: 'scale(0.85)',
    '.adyen-checkout__label__text': {
        color: 'webTypo.textSecondary'
    }
}

const getInputStyling = (theme) => {
    const LABEL_DEFAULT = getFloatingLabelDefault(theme)
    const LABEL_ACTIVE = FLOATING_LABEL_ACTIVE

    return {
    // Field container: relative for floating label positioning
    '.adyen-checkout__field': {
        position: 'relative',
        marginBottom: '12px'
    },
    // Floating label: default state (acts as placeholder inside the input)
    '.adyen-checkout__label': {
        ...LABEL_DEFAULT
    },
    // Also target labels inside the card form with higher specificity
    '.adyen-checkout__card__form .adyen-checkout__field .adyen-checkout__label': {
        ...LABEL_DEFAULT
    },
    // Floating label: floated up when field has value (Adyen adds --filled class)
    '.adyen-checkout__label.adyen-checkout__label--filled': {
        ...LABEL_ACTIVE
    },
    '.adyen-checkout__card__form .adyen-checkout__field .adyen-checkout__label.adyen-checkout__label--filled': {
        ...LABEL_ACTIVE
    },
    // Floating label: floated up when iframe field is focused (Adyen adds --focused class via JS)
    '.adyen-checkout__label.adyen-checkout__label--focused': {
        ...LABEL_ACTIVE
    },
    '.adyen-checkout__card__form .adyen-checkout__field .adyen-checkout__label.adyen-checkout__label--focused': {
        ...LABEL_ACTIVE
    },
    // Floating label: floated up on focus-within (regular inputs like holderName)
    '.adyen-checkout__field:focus-within .adyen-checkout__label': {
        ...LABEL_ACTIVE
    },
    '.adyen-checkout__card__form .adyen-checkout__field:focus-within .adyen-checkout__label': {
        ...LABEL_ACTIVE
    },
    // Floating label: floated up when regular input has a value (holderName after blur)
    ".adyen-checkout__field:has(input:not(:placeholder-shown)) .adyen-checkout__label": {
        ...LABEL_ACTIVE
    },
    ".adyen-checkout__field:has(input:-webkit-autofill) .adyen-checkout__label": {
        ...LABEL_ACTIVE
    },
    // Expiry date: always float label so MM/YY placeholder is visible
    '.adyen-checkout__field__exp-date .adyen-checkout__label, .adyen-checkout__field--expiryDate .adyen-checkout__label': {
        ...LABEL_ACTIVE,
        position: 'absolute !important'
    },
    '.adyen-checkout__card__form .adyen-checkout__field__exp-date .adyen-checkout__label, .adyen-checkout__card__form .adyen-checkout__field--expiryDate .adyen-checkout__label': {
        ...LABEL_ACTIVE,
        position: 'absolute !important'
    },
    // Floating label: error state — reset --filled label to default when not focused
    // Handles the case where Adyen adds --filled on empty fields during validation
    '.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled:not(.adyen-checkout__label--focused)': {
        top: '14px',
        transform: 'scale(1)'
    },
    // Expiry date: keep label floated even on error (high specificity to beat the reset rule)
    '.adyen-checkout__field__exp-date.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled, .adyen-checkout__field--expiryDate.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled': {
        ...LABEL_ACTIVE,
        position: 'absolute !important',
        top: '4px !important',
        transform: 'scale(0.85) !important'
    },
    // Card number & CVV (secured iframes): keep label floated even on error
    // so the value inside the iframe doesn't overlap with the label.
    '.adyen-checkout__field--cardNumber.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled, .adyen-checkout__field--securityCode.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled, .adyen-checkout__field__cvc.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--filled': {
        ...LABEL_ACTIVE,
        position: 'absolute !important',
        top: '4px !important',
        transform: 'scale(0.85) !important'
    },
    // Floating label: error + focused — keep floated
    '.adyen-checkout__field--error .adyen-checkout__label.adyen-checkout__label--focused': {
        ...LABEL_ACTIVE
    },
    // Floating label: error state label color
    '.adyen-checkout__field--error .adyen-checkout__label .adyen-checkout__label__text, .adyen-checkout__field--error .adyen-checkout__label .adyen-checkout__label__text--error': {
        color: 'webTypo.textError !important'
    },
    // Ensure asterisk stays textPrimary even on error labels
    '.adyen-checkout__field--error .adyen-checkout__label__text::after, .adyen-checkout__field--error .adyen-checkout__label__text--error::after': {
        color: 'webTypo.textPrimary !important'
    },
    // Hide placeholders on regular inputs (floating label replaces them)
    '.adyen-checkout__input::placeholder': {
        color: 'transparent'
    },
    // Input fields: match webTextInput design system variant
    '.adyen-checkout__input': {
        ...(theme?.textStyles?.bodyMdRegular || {}),
        height: '48px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: '0',
        borderColor: 'webTheme.strokeSecondary',
        backgroundColor: 'webTheme.fieldDefault',
        color: 'webTypo.textPrimary',
        paddingLeft: '8px',
        paddingRight: '8px',
        '&:hover': {
            borderColor: 'webTheme.strokeSecondary'
        },
        '&:focus, &:focus-visible': {
            borderColor: 'var(--chakra-colors-webTheme-strokeInteractive) !important',
            boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-strokeInteractive) !important',
            outline: 'none !important'
        },
        // Iframe fields: focused state (must come after :hover to win)
        '&.adyen-checkout__input--focus, &.adyen-checkout__input--focus:hover': {
            borderColor: 'var(--chakra-colors-webTheme-strokeInteractive) !important',
            boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-strokeInteractive) !important',
            outline: 'none !important'
        },
        '&.adyen-checkout__input--error': {
            borderColor: 'var(--chakra-colors-webTheme-strokeWarning)',
            boxShadow: 'none',
            '&:focus, &:focus-visible': {
                borderColor: 'var(--chakra-colors-webTheme-strokeWarning) !important',
                boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-supportError) !important'
            }
        },
        // Iframe fields: focused + error (must come after error to win)
        '&.adyen-checkout__input--focus.adyen-checkout__input--error, &.adyen-checkout__input--focus.adyen-checkout__input--error:hover': {
            borderColor: 'var(--chakra-colors-webTheme-strokeWarning) !important',
            boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-supportError) !important'
        }
    },
    // Regular inputs (holderName): add top padding to make room for floating label
    'input.adyen-checkout__input': {
        paddingTop: '22px',
        paddingBottom: '4px'
    },
    // Iframe wrapper spans (card number, expiry, CVV): no top padding so iframe fills full height and is clickable everywhere
    'span.adyen-checkout__input': {
        paddingTop: 0,
        paddingBottom: 0
    },
    // Hide the default Adyen "All fields are required" instruction
    '.adyen-checkout-form-instruction': {
        display: 'none'
    },
    // Hide expiry date hint icon
    '.adyen-checkout__field__exp-date_hint_wrapper': {
        display: 'none !important'
    },
    // Hide expiry date contextual text ("Front of card in MM/YY format") — hide all non-error spans
    '.adyen-checkout__field__exp-date .adyen-checkout-contextual-text:not(.adyen-checkout-contextual-text--error)': {
        display: 'none'
    },
    // CVV contextual text: always visible, even during error (copy comes from Adyen translations)
    '.adyen-checkout__field--securityCode .adyen-checkout-contextual-text:not(.adyen-checkout-contextual-text--error)': {
        ...(theme?.textStyles?.bodyXsRegular || {}),
        color: 'webTypo.textSecondary',
        display: 'block',
        marginTop: '8px',
        height: 'auto !important',
        opacity: '1 !important'
    },
    // Replace Adyen CVC hint SVGs with custom CVV icon
    '.adyen-checkout__card__cvc__hint': {
        display: 'none !important'
    },
    '.adyen-checkout__card__cvc__hint__wrapper': {
        backgroundImage: 'var(--custom-cvv-icon)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        width: '24px',
        height: '24px'
    },
    // Required field asterisks in card form (including holderName which sits outside .adyen-checkout__card__form)
    '.adyen-checkout__card__form .adyen-checkout__label__text::after, .adyen-checkout__card__holderName .adyen-checkout__label__text::after': {
        content: '" *"',
        color: 'webTypo.textPrimary'
    },
    // Error text styling — with error icon matching design system (ErrorIcon: 13x13 red square with !)
    '.adyen-checkout-contextual-text--error': {
        ...(theme?.textStyles?.bodyXsRegular || {}),
        color: 'webTypo.textError',
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        '&.adyen-checkout-contextual-text--hidden': {
            marginTop: '0',
            display: 'none'
        },
        '&::before': {
            content: '""',
            display: 'inline-block',
            width: '13px',
            height: '13px',
            minWidth: '13px',
            backgroundImage: 'var(--custom-error-icon)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat'
        }
    },
    // Error label styling
    '.adyen-checkout__label__text--error': {
        color: 'webTypo.textError'
    },
    '.adyen-checkout__input-wrapper': {
        border: '0 !important',
        boxShadow: 'none'
    },
    '.adyen-checkout__input-wrapper:active, .adyen-checkout__input-wrapper:active:hover, .adyen-checkout__input-wrapper:focus-within, .adyen-checkout__input-wrapper:focus-within:hover': {
        boxShadow: 'none !important',
        outline: 'none !important',
    },
    // Neutralize Adyen SDK's own outline on inputs so only our border+shadow applies
    '.adyen-checkout__input:focus, .adyen-checkout__input:active, .adyen-checkout__input.adyen-checkout__input--focus': {
        outline: 'none !important',
        borderColor: 'var(--chakra-colors-webTheme-strokeInteractive) !important',
        boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-strokeInteractive) !important'
    },
    '.adyen-checkout__input.adyen-checkout__input--error:focus, .adyen-checkout__input.adyen-checkout__input--error.adyen-checkout__input--focus': {
        outline: 'none !important',
        borderColor: 'var(--chakra-colors-webTheme-strokeWarning) !important',
        boxShadow: 'inset 0 0 0 1px var(--chakra-colors-webTheme-supportError) !important'
    }
}
}

const getKlarnaStyles = (theme) => ({
    '&.has-gc': {
        '.adyen-checkout__payment-method--klarna, .adyen-checkout__payment-method--klarna_account': {
            position: 'relative',
            pointerEvents: 'none',
            '.adyen-checkout__payment-method__name': {
                color: 'webTypo.textDisabled !important'
            },
            '.adyen-checkout__payment-method__radio': {
                borderColor: 'var(--chakra-colors-webTheme-iconDisabled) !important'
            },
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                background: 'webTheme.fieldDisabled'
            }
        }
    },
    '&.has-gc-products': {
        '.adyen-checkout__payment-method--klarna, .adyen-checkout__payment-method--klarna_account': {
            position: 'relative',
            pointerEvents: 'none',
            '.adyen-checkout__payment-method__name': {
                color: 'webTypo.textDisabled !important'
            },
            '.adyen-checkout__payment-method__radio': {
                borderColor: 'var(--chakra-colors-webTheme-iconDisabled) !important'
            },
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                background: 'webTheme.fieldDisabled'
            }
        }
    },
})

const getCheckboxStyling = (theme) => ({
    '.adyen-checkout__store-details': {
        border: 0,
        padding: 0,
        margin: 0,
        marginTop: '4px',
        marginBottom: '16px'
    },
    '.adyen-checkout__store-details .adyen-checkout__checkbox__input': {
        width: '15px !important',
        height: '15px !important',
        flexShrink: 0,
        borderRadius: '0 !important',
        border: '1px solid !important',
        borderColor: 'var(--chakra-colors-webTheme-iconPrimary) !important',
        appearance: 'none !important',
        opacity: '1 !important',
        position: 'relative !important',
        pointerEvents: 'auto !important',
        cursor: 'pointer',
        '&:checked': {
            backgroundColor: 'var(--chakra-colors-webTheme-iconPrimary)',
            borderColor: 'var(--chakra-colors-webTheme-iconPrimary)',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: '4px',
                top: '1px',
                width: '5px',
                height: '9px',
                border: 'solid white',
                borderWidth: '0 2px 2px 0',
                transform: 'rotate(45deg)'
            }
        }
    },
    '.adyen-checkout__store-details .adyen-checkout__checkbox__label': {
        ...(theme?.textStyles?.bodyXsRegular || {}),
        paddingLeft: '8px',
        cursor: 'pointer',
        '&::before': {
            display: 'none !important'
        },
        '&::after': {
            display: 'none !important'
        }
    }
})

const SAVE_CARDS_CARD = (theme) => ({
    '.adyen-checkout__payment-methods-list--storedPayments': {
        gap: 4,
        '& .adyen-checkout__payment-method__header__title--standalone': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: '8px',
            '&::before': {
                content: '""',
                width: '16px',
                height: '16px',
                minWidth: '16px',
                margin: '4px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderRadius: '50%',
                borderColor: 'var(--chakra-colors-webTheme-iconPrimary)',
                backgroundColor: 'var(--chakra-colors-webTheme-backgroundDefault)'
            }
        },
        '& .adyen-checkout__payment-method--selected .adyen-checkout__payment-method__header__title--standalone::before': {
            background: 'radial-gradient(circle, var(--chakra-colors-webTheme-iconPrimary) 4px, var(--chakra-colors-webTheme-backgroundDefault) 4px)'
        },
        '& .adyen-checkout__payment-method__header__title': {
            minHeight: '28px'
        },
        '& .adyen-checkout__payment-method--selected': {
            backgroundColor: theme?.colors?.webTheme?.backgroundPrimaryBrand,
            '& .adyen-checkout-pm-details-wrapper': {
                marginTop: '-8px'
            }
        },
        // For stored cards: show brand icon between radio and last-four text
        '& .adyen-checkout__payment-method__image__wrapper': {
            order: 1,
            marginLeft: '0',
            boxShadow: 'none !important'
        },
        // Undo the custom combined card icon for stored cards — show default Adyen brand image per card
        '& .adyen-checkout__payment-method--scheme .adyen-checkout__payment-method__image__wrapper': {
            backgroundImage: 'none !important',
            width: 'auto',
            height: 'auto'
        },
        '& .adyen-checkout__payment-method--scheme .adyen-checkout__payment-method__image': {
            display: 'block !important'
        },
        '& .adyen-checkout__payment-method__name_wrapper': {
            order: 2,
            marginLeft: '0'
        }
    }
})

export default {
    parts: ['heading', 'container', 'recaptchaText', 'redirectNotice'],
    baseStyle: ({wrapperClass, theme}) => ({
        heading: {
            textStyle: 'webH4',
            mb: {base: 3, lg: 4}
        },
        container: {
            [`.${wrapperClass}`]: {
                ...ADYEN_API_VARS,
                ...RESET_STYLING,
                ...GLOBAL_STYLES,
                ...THIRD_PARTY_SELECTED_PANEL_STYLING,
                ...SECTION_HEADER_STYLING(theme),
                ...RADIO_STYLING,
                ...HEADER_LAYOUT_STYLING(theme),
                ...ICON_STYLING,
                ...getInputStyling(theme),
                ...getKlarnaStyles(theme),
                ...getPaymentButtonStyling(theme),
                ...getCheckboxStyling(theme),
                ...SAVE_CARDS_CARD(theme)
            },
        },
        recaptchaText: {
            textStyle: 'bodyXsRegular',
            color: 'webTypo.textTertiary',
            pt: 2,
            a: {
                textDecoration: 'underline',
                textStyle: 'bodyXsRegular',
                color: 'webTypo.textTertiary'
            }
        },
        redirectNotice: {
            textStyle: 'bodyXsSRegular',
            color: 'webTypo.textSecondary',
            mb: 4,
            pl: 9
        }
    })
}
